/*eslint-disable no-console*/
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ACCOUNT_CREATION_SUCCESSFUL } from "../Constants/CommonConstants";
import {
  API_INITIAL_STATE,
  BUILDER_TYPE_FULFILLED,
  BUILDER_TYPE_PENDING,
  BUILDER_TYPE_REJECTED,
  SLICE_PROVIDER_ACCOUNT_CREATION,
  THUNK_GET_PROVIDER,
  THUNK_POST_PROVIDER_CREATE_ACCOUNT,
  THUNK_POST_PROVIDER_OTP,
  THUNK_POST_PROVIDER_PASSWORD,
  THUNK_POST_PROVIDER_RESEND_OTP,
  THUNK_POST_PROVIDER_USERNAME,
  THUNK_POST_PROVIDER_VALIDATE_OTP,
  THUNK_POST_VERIFY_PROVIDER_DETAILS,
} from "../Constants/SliceAndThunkConstants";
import { OTP_SENT_SUCCESSFULLY } from "../Constants/VerifyAccountConstants";
import {
  apiSliceBuilderService,
  fetchUserRoleByValue,
  getProviderLocationIdList,
} from "../Services/CommonServices";
import {
  getProviderService,
  postProviderCreateAccountService,
  postProviderOTPService,
  postProviderPasswordService,
  postProviderUsernameService,
  postVerifyProviderDetailsService,
} from "../Services/ProviderAccountCreationService";

const initialState = {
  confirmSiteAccessDetails: {
    accessCode: "",
    locationNPI: "",
    response: {},
    userInvitationId: "",
    isAccountCreated: false,
    isVerified: false,
    isAccessCodeExpired: false,
  },
  providerPersonalInfo: {
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    providerSiteLocationId: "",
    accountType: "",
    emailLangCd: "",
  },
  siteDetails: [],
  accountData: {
    emailLanguage: "",
    profLicenseNo: "",
    hasProfLicenseNo: "",
    dob: "",
    username: "",
    password: "",
    confirmPassword: "",
    usernameApiResponse: "",
    passwordApiResponse: "",
  },
  authToken: "",
  validateOTPDescription: "",
  sendOTPDescription: "",
  accountCreationStatus: "",
  postVerifyProviderDetailsResponse: API_INITIAL_STATE,
  getProviderResponse: API_INITIAL_STATE,
  postProviderUsernameResponse: API_INITIAL_STATE,
  postProviderPasswordResponse: API_INITIAL_STATE,
  postProviderOTPResponse: API_INITIAL_STATE,
  postProviderResendOTPResponse: API_INITIAL_STATE,
  postProviderValidateOTPResponse: API_INITIAL_STATE,
  postProviderCreateAccountResponse: API_INITIAL_STATE,
};

export const postVerifyProviderDetails = createAsyncThunk(
  `${SLICE_PROVIDER_ACCOUNT_CREATION}/${THUNK_POST_VERIFY_PROVIDER_DETAILS}`,
  async (payload) => {
    const response = await postVerifyProviderDetailsService(payload);
    return response;
  }
);

export const getProvider = createAsyncThunk(
  `${SLICE_PROVIDER_ACCOUNT_CREATION}/${THUNK_GET_PROVIDER}`,
  async (payload) => {
    const response = await getProviderService(payload);
    return response;
  }
);

export const postProviderUsername = createAsyncThunk(
  `${SLICE_PROVIDER_ACCOUNT_CREATION}/${THUNK_POST_PROVIDER_USERNAME}`,
  async ({ data, apiAction }, { rejectWithValue }) => {
    try {
      return await postProviderUsernameService(data, apiAction);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postProviderPassword = createAsyncThunk(
  `${SLICE_PROVIDER_ACCOUNT_CREATION}/${THUNK_POST_PROVIDER_PASSWORD}`,
  async ({ data, apiAction }, { rejectWithValue }) => {
    try {
      return await postProviderPasswordService(data, apiAction);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postProviderCreateAccount = createAsyncThunk(
  `${SLICE_PROVIDER_ACCOUNT_CREATION}/${THUNK_POST_PROVIDER_CREATE_ACCOUNT}`,
  async (payload) => {
    const response = await postProviderCreateAccountService(payload);
    return response;
  }
);

export const postProviderOTP = createAsyncThunk(
  `${SLICE_PROVIDER_ACCOUNT_CREATION}/${THUNK_POST_PROVIDER_OTP}`,
  async (payload, { getState }) => {
    const accountType = fetchUserRoleByValue(
      getState().referenceData.referenceOptions.userRole,
      getState().providerAccountCreation?.providerPersonalInfo?.accountType
    )?.valueText;
    const response = await postProviderOTPService(payload, accountType);
    return response;
  }
);

export const postProviderResendOTP = createAsyncThunk(
  `${SLICE_PROVIDER_ACCOUNT_CREATION}/${THUNK_POST_PROVIDER_RESEND_OTP}`,
  async (payload, { getState }) => {
    const accountType = fetchUserRoleByValue(
      getState().referenceData.referenceOptions.userRole,
      getState().providerAccountCreation?.providerPersonalInfo?.accountType
    )?.valueText;
    const response = await postProviderOTPService(payload, accountType);
    return response;
  }
);

export const postProviderValidateOTP = createAsyncThunk(
  `${SLICE_PROVIDER_ACCOUNT_CREATION}/${THUNK_POST_PROVIDER_VALIDATE_OTP}`,
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const accountType = fetchUserRoleByValue(
        getState().referenceData.referenceOptions.userRole,
        getState().providerAccountCreation?.providerPersonalInfo?.accountType
      )?.valueText;
      const response = await postProviderOTPService(payload, accountType);

      if (
        response?.data?.message?.description === ACCOUNT_CREATION_SUCCESSFUL
      ) {
        const providerPersonalInfo =
          getState().providerAccountCreation?.providerPersonalInfo;

        const providerSiteDetails =
          getState().providerAccountCreation?.siteDetails;

        const accountData = getState().providerAccountCreation?.accountData;

        const userInvitationId =
          getState().providerAccountCreation?.confirmSiteAccessDetails
            .userInvitationId;

        const payload = {
          firstName: providerPersonalInfo.firstName,
          lastName: providerPersonalInfo.lastName,
          middleName: providerPersonalInfo.middleName,
          username: accountData.username,
          userRole: providerPersonalInfo.accountType,
          dateOfBirth: accountData.dob,
          userInvitationId: userInvitationId,
          providerSiteLocationIdList:
            getProviderLocationIdList(providerSiteDetails),
          isProfLicenseNoRequired: accountData?.hasProfLicenseNo,
        };

        if (accountData?.profLicenseNo) {
          payload.profLicenseNo = accountData.profLicenseNo;
        }
        await dispatch(postProviderCreateAccount(payload));
      }
      return response;
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const providerAccountCreation = createSlice({
  name: "providerAccountCreation",
  initialState,
  reducers: {
    saveSiteAccess: (state, action) => {
      state.confirmSiteAccessDetails.accessCode = action.payload.accessCode;
      state.confirmSiteAccessDetails.locationNPI = action.payload.locationNPI;
      state.providerPersonalInfo = initialState.providerPersonalInfo;
      state.siteDetails = initialState.siteDetails;
      state.accountData = initialState.accountData;
    },

    saveProviderAccountData: (state, action) => {
      state.accountData.profLicenseNo = action.payload.profLicenseNo;
      state.accountData.dob = action.payload.dateOfBirth;
      state.accountData.username = action.payload.userName;
      state.accountData.password = action.payload.password;
      state.accountData.confirmPassword = action.payload.confirmPassword;
      state.accountData.emailLanguage = action.payload.emailLanguage;
      state.accountData.hasProfLicenseNo = action?.payload?.hasProfLicenseNo;
    },
    clearAccountCreationStatus: (state) => {
      state.accountCreationStatus = "";
    },
    clearProviderAccountCreationApiResponse: (state, action) => {
      const responseName = `${action.payload}Response`;
      if (!initialState[responseName]) {
        return;
      }
      state[responseName] = initialState[responseName];
    },
    resetProviderValidateOTPDescription: (state) => {
      state.validateOTPDescription = "";
    },
    resetProviderAccountSendOTPDescription: (state) => {
      state.sendOTPDescription = "";
    },
    clearProviderAccountCreationSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(postVerifyProviderDetails.pending, (state) => {
      state.postVerifyProviderDetailsResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postVerifyProviderDetails.fulfilled, (state, action) => {
      state.postVerifyProviderDetailsResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED
      );
      state.confirmSiteAccessDetails.userInvitationId =
        action?.payload?.data?.data?.userInvitationId;
      state.confirmSiteAccessDetails.isAccountCreated =
        action?.payload?.data?.data?.isAccountCreated;
      state.confirmSiteAccessDetails.isVerified =
        action?.payload?.data?.data?.isVerified;
      state.confirmSiteAccessDetails.isAccessCodeExpired =
        action?.payload?.data?.data?.isAccessCodeExpired;
    });

    builder.addCase(getProvider.pending, (state) => {
      state.getProviderResponse = apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(getProvider.fulfilled, (state, action) => {
      state.getProviderResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED
      );

      state.providerPersonalInfo.firstName =
        action?.payload?.data?.data?.firstName;
      state.providerPersonalInfo.middleName =
        action?.payload?.data?.data?.middleName;
      state.providerPersonalInfo.lastName =
        action?.payload?.data?.data?.lastName;
      state.providerPersonalInfo.email = action?.payload?.data?.data?.email;
      state.providerPersonalInfo.providerSiteLocationId =
        action?.payload?.data?.data?.providerSiteLocationId;
      state.providerPersonalInfo.accountType =
        action?.payload?.data?.data?.accountType;
      state.providerPersonalInfo.emailLangCd =
        action?.payload?.data?.data?.preferredEmailLangCd;
      state.siteDetails =
        action?.payload?.data?.data?.providerSiteLocationDtoList;
    });

    builder.addCase(postProviderUsername.pending, (state) => {
      state.postProviderUsernameResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postProviderUsername.rejected, (state, action) => {
      if (action?.payload?.status === 406) {
        state.postProviderUsernameResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED
        );
        return;
      }
      throw new Error(action?.payload?.data.error);
    });
    builder.addCase(postProviderUsername.fulfilled, (state, action) => {
      state.postProviderUsernameResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });

    builder.addCase(postProviderPassword.pending, (state) => {
      state.postProviderPasswordResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postProviderPassword.rejected, (state, action) => {
      if (action?.payload?.status === 406) {
        state.postProviderPasswordResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
        return;
      }
      throw new Error(action?.payload?.data.error);
    });
    builder.addCase(postProviderPassword.fulfilled, (state, action) => {
      state.postProviderPasswordResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });

    builder.addCase(postProviderOTP.pending, (state) => {
      state.postProviderOTPResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postProviderOTP.fulfilled, (state, action) => {
      state.postProviderOTPResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      const { data } = action.payload.data;
      if (
        state.postProviderOTPResponse.message.description ===
        OTP_SENT_SUCCESSFULLY
      ) {
        state.authToken = data?.authToken;
      }
    });

    builder.addCase(postProviderResendOTP.pending, (state) => {
      state.postProviderResendOTPResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postProviderResendOTP.fulfilled, (state, action) => {
      state.postProviderResendOTPResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      const { authToken } = action.payload.data.data;
      state.sendOTPDescription =
        state?.postProviderResendOTPResponse?.message?.description;
      state.authToken = authToken;
    });

    builder.addCase(postProviderValidateOTP.pending, (state) => {
      state.postProviderValidateOTPResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postProviderValidateOTP.rejected, (state, action) => {
      if (action?.payload?.status === 406) {
        state.postProviderValidateOTPResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
        const validateOTPDescription = state?.postProviderValidateOTPResponse
          ?.error?.message
          ? state?.postProviderValidateOTPResponse?.error?.message
          : state?.postProviderValidateOTPResponse?.message?.description;
        state.validateOTPDescription = validateOTPDescription;

        if (action?.payload?.data?.data?.authToken) {
          state.authToken = action?.payload?.data?.data?.authToken;
        }
        return;
      }
      throw new Error(action?.payload?.data.error);
    });
    builder.addCase(postProviderValidateOTP.fulfilled, (state, action) => {
      state.postProviderValidateOTPResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });

    builder.addCase(postProviderCreateAccount.pending, (state) => {
      state.postProviderCreateAccountResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postProviderCreateAccount.fulfilled, (state, action) => {
      state.postProviderCreateAccountResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
  },
});

export const {
  saveSiteAccess,
  saveProviderAccountData,
  clearAccountCreationStatus,
  clearProviderAccountCreationApiResponse,
  resetProviderValidateOTPDescription,
  clearProviderAccountCreationSlice,
  resetProviderAccountSendOTPDescription,
} = providerAccountCreation.actions;

export default providerAccountCreation.reducer;
